import React, { JSX } from 'react';

interface State {
  npoId?: string;
  initialNpoMerchantData?: string;
  completed: boolean;
}

interface SetNpoId {
  type: 'SET_NPO_ID';
  npoId: string;
}

interface SetInitialNpoMerchantData {
  type: 'SET_INITIAL_NPO_MERCHANT_DATA';
  initialNpoMerchantData: string;
}

interface CompleteOnboarding {
  type: 'COMPLETE_MERCHANT_ONBOARDING';
}

type Action = SetNpoId | SetInitialNpoMerchantData | CompleteOnboarding;

type Dispatch = (action: Action) => void;

interface NpoMerchantOnboardingStateContextData {
  state: State;
  dispatch: Dispatch;
  setNpoId: (npoId: string) => void;
  setInitialNpoMerchantData: (initialNpoData: string) => void;
  completeMerchantOnboarding: () => void;
}

const defaultValues: State = {
  npoId: undefined,
  initialNpoMerchantData: undefined,
  completed: false
};

const NpoMerchantOnboardingStateContext = React.createContext<NpoMerchantOnboardingStateContextData | undefined>(
  undefined
);
function npoLoginScreenStateReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_NPO_ID':
      return {
        ...state,
        npoId: action.npoId
      };

    case 'SET_INITIAL_NPO_MERCHANT_DATA':
      return {
        ...state,
        initialNpoMerchantData: action.initialNpoMerchantData
      };

    case 'COMPLETE_MERCHANT_ONBOARDING':
      return {
        ...state,
        completed: true
      };

    default:
      throw new Error(`Unhandled action type`);
  }
}

function NpoMerchantOnboardingStateProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(npoLoginScreenStateReducer, {
    npoId: defaultValues.npoId,
    initialNpoMerchantData: defaultValues.initialNpoMerchantData,
    completed: defaultValues.completed
  });

  const setNpoId = (npoId: string) => {
    dispatch({
      type: 'SET_NPO_ID',
      npoId: npoId
    });
  };

  const setInitialNpoMerchantData = (initialNpoMerchantData: string) => {
    dispatch({
      type: 'SET_INITIAL_NPO_MERCHANT_DATA',
      initialNpoMerchantData: initialNpoMerchantData
    });
  };

  const completeMerchantOnboarding = () => {
    dispatch({
      type: 'COMPLETE_MERCHANT_ONBOARDING'
    });
  };

  return (
    <NpoMerchantOnboardingStateContext.Provider
      value={{
        state,
        dispatch,
        setNpoId,
        setInitialNpoMerchantData,
        completeMerchantOnboarding
      }}>
      {children}
    </NpoMerchantOnboardingStateContext.Provider>
  );
}

function useNpoMerchantOnboardingState(): NpoMerchantOnboardingStateContextData {
  const context = React.useContext(NpoMerchantOnboardingStateContext);
  if (context === undefined) {
    throw new Error('useNpoMerchantOnboardingState must be used within a NpoMerchantOnboardingStateProvider');
  }
  return context;
}

export { NpoMerchantOnboardingStateProvider, useNpoMerchantOnboardingState };
